import type { FunctionComponent } from 'react';
import { ButtonPrimary } from '@/modules/common/components/buttons';
import { PageLayout } from '@/modules/common/components/PageLayout';
import { useTranslation } from 'react-i18next';
import { MainCard } from '@/modules/common/components/mainCard';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { TextInput } from '@/modules/common/components/inputs';
import { useMutation } from '@tanstack/react-query';
import DendreoSyncInvoiceRequest from '@spraypaint/dendreo/DendreoSyncInvoiceRequest';

export type SyncInvoiceFormData = {
  invoiceNumber: string;
};

function SyncInvoiceForm({
  onSubmit,
  isLoading,
}: {
  onSubmit: (data: SyncInvoiceFormData) => Promise<unknown>;
  isLoading: boolean;
}) {
  const { register, handleSubmit } = useForm<SyncInvoiceFormData>();
  const { t } = useTranslation();

  const handleOnSubmit = async (data: SyncInvoiceFormData) => {
    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)}>
      <TextInput
        {...register('invoiceNumber', { required: true })}
        className="mt-[33px]"
        label={t(
          'activerecord.attributes.dendreo_sync_invoice_request.invoice_number',
          {
            ns: 'rails',
            defaultValue: 'Invoice Number',
          },
        )}
        required
      />
      <ButtonPrimary isLoading={isLoading} className="mt-[51px]" type="submit">
        {t('front.defaults.form.submit', { defaultValue: 'Submit' })}
      </ButtonPrimary>
    </form>
  );
}

export const DendreoSyncInvoice: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (data: SyncInvoiceFormData) => {
      const syncInvoiceRequest = new DendreoSyncInvoiceRequest({
        invoiceNumber: data.invoiceNumber,
      });
      return syncInvoiceRequest.saveOrThrow();
    },
    onSuccess: () => {
      navigate('../logs');
    },
  });

  return (
    <PageLayout
      Title={t('front.dendreo.sync_invoice.header.title', {
        defaultValue: 'Sync Invoice with Dendreo',
      })}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MainCard
          header={t('front.dendreo.sync_invoice.form.title', {
            defaultValue: 'Sync Invoice',
          })}
        >
          <SyncInvoiceForm onSubmit={mutateAsync} isLoading={isLoading} />
        </MainCard>
      </div>
    </PageLayout>
  );
};
